import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { mainApi } from './api/emptyApi';
import { historyApi } from './api/emptyHistoryApi';
import { jackpotApi } from './api/emptyJackpotApi';
import { gameApi } from './api/emptyGameApi';
import userReducer from './slices/userSlice';
import gameReducer from './slices/gameSlice';
import statesReducer from './slices/statesSlice';
import { autoplayReducer } from './slices/autoplay';
import persistValuesReducer from './slices/persistValuesSlice';
import lotteryReducer from '../../features/LotteryGame/store/lotterySlice';
import userJackpotReducer from '../../pages/JackpotPage/store/jackpotSlice';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import tutorialReducer from '../../features/Tutorial/store/tutorialSlice';

import storage from 'redux-persist/lib/storage';
import listenerMiddleware from './listenerMiddleware';
import { persistReducer, persistStore } from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/es/stateReconciler/autoMergeLevel2';
import { settingsReducer } from './slices/settings';
import featureFlagsReducer from '../../entities/featureFlag/model/featureFlagsSlice';
import lotteryGameCreationReducer from '../../features/LotteryGame/store/lotteryGameCreationSlice';
import { rtkQueryErrorLogger } from './api/rtkQueryErrorLogger';

const rootReducer = combineReducers({
  [mainApi.reducerPath]: mainApi.reducer,
  [jackpotApi.reducerPath]: jackpotApi.reducer,
  [historyApi.reducerPath]: historyApi.reducer,
  [gameApi.reducerPath]: gameApi.reducer,
  user: userReducer,
  game: gameReducer,
  states: statesReducer,
  lottery: lotteryReducer,
  persistValues: persistValuesReducer,
  jackpot: userJackpotReducer,
  autoplay: autoplayReducer,
  settings: settingsReducer,
  featureFlags: featureFlagsReducer,
  lotteryGameCreation: lotteryGameCreationReducer,
  tutorial: tutorialReducer,
});

const rootPersistConfig = {
  storage,
  key: 'root',
  autoMergeLevel2,
  keyPrefix: 'redux-',
  whitelist: ['persistValues'],
};

export const store = configureStore({
  reducer: persistReducer(rootPersistConfig, rootReducer),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    })
      .concat(rtkQueryErrorLogger)
      .concat(mainApi.middleware)
      .concat(jackpotApi.middleware)
      .concat(historyApi.middleware)
      .concat(gameApi.middleware)
      .concat(listenerMiddleware.middleware),
});

persistStore(store);

export default store;

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof rootReducer>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
